/**
 * The table creator's names, types and defaults (DESIGN_EDITING.md E5, E22): turning a typed name into a safe one,
 * the type menu (plain words, with more under Expert mode), and which defaults each type offers.
 *
 * ⚠️ NO IMPORTS, ON PURPOSE (dataView.ts says why).
 */

/** The common reserved words, mirrored from the backend's newPgName, so the console can say "Reserved word." at once. */
const RESERVED = new Set([
    'select', 'from', 'where', 'table', 'column', 'order', 'group', 'user', 'default', 'primary', 'key', 'and', 'or',
    'not', 'null', 'true', 'false', 'insert', 'update', 'delete', 'create', 'drop', 'alter', 'join', 'on', 'as', 'in',
    'is', 'case', 'when', 'then', 'else', 'end', 'all', 'any', 'check', 'unique', 'references', 'constraint', 'index',
    'grant', 'values', 'into', 'distinct', 'having', 'limit', 'offset', 'union', 'using', 'with', 'to', 'set',
]);

/** Turn a typed name into lower snake case, within 63 bytes: letters and digits kept, runs of anything else one _. */
export const toSnakeCase = (s: string): string => {
    const lower = s.trim().toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');
    let out = lower.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (out && /^[0-9]/.test(out)) out = '_' + out;
    return out.slice(0, 63);
};

export type NameProblem = 'required' | 'shape' | 'reserved';

/** A new name's problem for instant feedback; the helper's pg_get_keywords check is the authority on reserved words. */
export const nameProblem = (name: string): NameProblem | null => {
    if (name === '') return 'required';
    if (!/^[a-z_][a-z0-9_]{0,62}$/.test(name) || name.startsWith('pg_')) return 'shape';
    if (RESERVED.has(name)) return 'reserved';
    return null;
};

export const nameProblemLine = (p: NameProblem): string =>
    p === 'required' ? 'Name is required.' : p === 'reserved' ? 'Reserved word.' : 'Use lowercase letters, numbers and underscores.';

// ── Types ────────────────────────────────────────────────────────────────────────────────────────────────────────

export type TypeToken = 'text' | 'bigint' | 'integer' | 'numeric' | 'double' | 'boolean' | 'date' | 'timestamptz' | 'timestamp' | 'time' | 'uuid' | 'jsonb' | 'bytea';

export interface TypeOption { token: TypeToken; label: string; expert?: boolean }

/** The type menu: plain words first, the rest only under Expert mode (E22). */
export const TYPE_OPTIONS: TypeOption[] = [
    { token: 'text', label: 'Text' },
    { token: 'bigint', label: 'Number' },
    { token: 'numeric', label: 'Decimal' },
    { token: 'boolean', label: 'Yes/No' },
    { token: 'date', label: 'Date' },
    { token: 'timestamptz', label: 'Date and time' },
    { token: 'jsonb', label: 'JSON' },
    { token: 'uuid', label: 'UUID' },
    { token: 'integer', label: 'Integer', expert: true },
    { token: 'double', label: 'Double', expert: true },
    { token: 'timestamp', label: 'Timestamp without zone', expert: true },
    { token: 'time', label: 'Time', expert: true },
    { token: 'bytea', label: 'Bytes', expert: true },
];

export const typeOptions = (expert: boolean): TypeOption[] => TYPE_OPTIONS.filter((o) => expert || !o.expert);
export const typeLabel = (token: TypeToken): string => TYPE_OPTIONS.find((o) => o.token === token)?.label ?? token;

// ── Defaults ─────────────────────────────────────────────────────────────────────────────────────────────────────

export type DefaultKind = 'none' | 'value' | 'now' | 'today' | 'random_uuid' | 'identity';

export interface DefaultOption { kind: DefaultKind; label: string }

/**
 * The defaults a type offers. `atCreate` adds the ones that may only be set when the table is made (an automatic id,
 * a random id); a key column of a whole-number type offers the automatic id.
 */
export const defaultOptions = (token: TypeToken, opts: { atCreate: boolean; isKey: boolean }): DefaultOption[] => {
    const out: DefaultOption[] = [{ kind: 'none', label: 'None' }];
    if (opts.atCreate && opts.isKey && (token === 'bigint' || token === 'integer')) out.push({ kind: 'identity', label: 'Automatic number' });
    if (opts.atCreate && token === 'uuid') out.push({ kind: 'random_uuid', label: 'Random id' });
    if (token === 'timestamptz' || token === 'timestamp') out.push({ kind: 'now', label: 'Now' });
    if (token === 'date') out.push({ kind: 'today', label: 'Today' });
    out.push({ kind: 'value', label: 'A fixed value' });
    return out;
};

export const defaultLabel = (kind: DefaultKind): string =>
    ({ none: 'None', value: 'A fixed value', now: 'Now', today: 'Today', random_uuid: 'Random id', identity: 'Automatic number' })[kind];

// ── A column being designed in the New table modal ───────────────────────────────────────────────────────────────

export interface DraftColumn {
    name: string;
    type: TypeToken;
    required: boolean;
    isKey: boolean;
    defaultKind: DefaultKind;
    defaultValue: string;
    /** The id/created_at seed rows: their key/type cannot be changed, only removed (except id's key). */
    locked?: boolean;
    /** A column that came from a CSV header (flow 3): its type is editable, but it cannot be removed, so the file's
     *  columns always match the table that is created. */
    fromCsv?: boolean;
}

/** The two rows a new table starts with: an automatic id key, and a created_at set to Now. */
export const seedColumns = (): DraftColumn[] => [
    { name: 'id', type: 'bigint', required: true, isKey: true, defaultKind: 'identity', defaultValue: '', locked: true },
    { name: 'created_at', type: 'timestamptz', required: true, isKey: false, defaultKind: 'now', defaultValue: '' },
];

export type CreateProblem = { line: string } | null;

/** What a draft table would be refused for, before anything is sent: names, uniqueness, a key, a value default. */
export const createProblem = (table: string, columns: DraftColumn[]): CreateProblem => {
    const t = nameProblem(table);
    if (t) return { line: t === 'required' ? 'Name is required.' : nameProblemLine(t) };
    if (columns.length === 0) return { line: 'Add at least one column.' };
    const seen = new Set<string>();
    for (const c of columns) {
        const p = nameProblem(c.name);
        if (p) return { line: nameProblemLine(p) };
        if (seen.has(c.name)) return { line: 'Names must be unique.' };
        seen.add(c.name);
        if (c.defaultKind === 'value' && c.defaultValue === '') return { line: `${c.name} needs a default value, or choose None.` };
        if (c.defaultKind === 'value' && [...c.defaultValue].length > 1000) return { line: `${c.name}'s default is too long.` };
    }
    if (!columns.some((c) => c.isKey)) return { line: 'Choose a key column.' };
    if (columns.filter((c) => c.isKey).length > 8) return { line: 'A key has at most 8 columns.' };
    return null;
};

/** The create_table request body from the draft: an identity key when the one key column is an automatic id. */
export const createBody = (writeRef: string, table: string, columns: DraftColumn[]): Record<string, unknown> => {
    const keyCols = columns.filter((c) => c.isKey);
    // The identity-key sugar is only for the seed `id` column; a renamed auto-id key is sent as a named-column key,
    // so the server keeps the name the user chose (review round 6, NIT 2).
    const identity = keyCols.length === 1 && keyCols[0].defaultKind === 'identity' && keyCols[0].name === 'id';
    const body: Record<string, unknown> = {
        write_ref: writeRef,
        table,
        columns: columns
            .filter((c) => !(identity && c.defaultKind === 'identity'))
            .map((c) => ({
                name: c.name,
                type: c.type,
                ...(c.required ? { not_null: true } : {}),
                default: c.defaultKind === 'none' ? { kind: 'none' }
                    : c.defaultKind === 'value' ? { kind: 'value', value: c.defaultValue }
                    : { kind: c.defaultKind },
            })),
        key: identity ? { kind: 'identity' } : { kind: 'columns', columns: keyCols.map((c) => c.name) },
    };
    return body;
};
